import { useCallback } from "react";
import type { BaseEntity } from "../../../UniversalCRMTable/types";
import { useDetailNavigation } from "@/contexts/DetailNavigationContext";
import { useOpenModal } from "@/lib/modals";
import { getModalConfig, isModalConfigured } from "@/lib/modals/url/modal-url-config";

/**
 * Composition hook for Detail Modal Navigation
 * Manages detail modal navigation and URL updates
 * 
 * UPDATED: Now uses the type-safe modal URL system from @/lib/modals
 * with proper entity parameter mapping from MODAL_URL_CONFIG
 *
 * Job Duty: Manage detail modal navigation
 */
export function useCompositionDetailModal<T extends BaseEntity>(
  entityName: string,
  _navigation: unknown
) {
  const { setContext } = useDetailNavigation();
  const openModal = useOpenModal();

  const handleOpenDetailModal = useCallback(
    (item: T, ids: string[], index: number) => {
      if (!item?.id) {
        console.warn("[useCompositionDetailModal] Cannot open modal: item.id is missing", item);
        return;
      }

      // Set navigation context
      setContext(ids, index, entityName);

      // Open modal using the type-safe system with correct parameter name from config
      const modalId = `${entityName}-detail`;
      if (!isModalConfigured(modalId)) {
        return;
      }
      
      // Get the correct parameter names from MODAL_URL_CONFIG
      // This ensures we use the exact parameter name defined in the config
      const config = getModalConfig(modalId);
      
      // Use the plural form as that's what buildModalUrl expects
      const params = {
        [config.pluralParam]: item.id,
      };
      
      openModal(modalId, params);
    },
    [entityName, setContext, openModal]
  );

  return {
    handleOpenDetailModal,
  };
}
